/**
 * The applicant-facing API client. Deliberately tiny — this ships to people on Nigerian mobile
 * networks and counts against a 150 KB gzipped budget (docs/01-PRODUCT-SPEC.md §13).
 */

export interface PublicField {
  key: string;
  type: string;
  label: string;
  helpText?: string | null;
  placeholder?: string | null;
  position: number;
  rowGroup: number;
  widthSpan: number;
  isRequired: boolean;
  isReadOnly: boolean;
  visibility: "visible" | "hidden" | "admin_only";
  config: Record<string, unknown>;
  sectionKey: string | null;
  /** True only for the field bound to person.email — see FormFlow.tsx's identity lock. */
  isIdentityField: boolean;
  /** Present when this field is bound to one half of a state/LGA attribute pair (origin, school or
   *  address) — see FormFlow.tsx's cascading-options handling. */
  stateLgaPairing: { role: "state" | "lga"; group: string } | null;
}

export interface PublicSection {
  key: string;
  title: string | null;
  description: string | null;
  pageNumber: number;
  position: number;
}

export interface PublicFormResponse {
  /** Present and true only from fetchFormPreview — see PreviewFlow.tsx. */
  preview?: boolean;
  form: {
    id: string;
    slug: string;
    title: string;
    description: string | null;
    programme: { name: string };
    cycle: { label: string } | null;
    requiresIdentity: boolean;
    allowDrafts: boolean;
    reviewBeforeSubmit: boolean;
    turnstileSiteKey: string | null;
  };
  availability: {
    state:
      "OPEN" | "NOT_YET_OPEN" | "CLOSED_SCHEDULE" | "CLOSED_MANUAL" | "FULL" | "ARCHIVED" | "DRAFT";
    opensAt: string | null;
    closesAt: string | null;
    message: string | null;
    redirectUrl: string | null;
  };
  branding: {
    colors: Record<string, string>;
    logoLayout: string;
    primaryLogoUrl: string | null;
    secondaryLogoUrl: string | null;
    fonts: { heading: string; body: string };
    footer: { text: string | null; privacyUrl: string | null; dpoContact: string | null };
  };
  privacyNotice?: string | null;
  /** Absent entirely when the form is not open — a closed form ships no questions. */
  schema?: {
    versionId: string;
    sections: PublicSection[];
    fields: PublicField[];
    logic: unknown;
  };
}

export interface SubmitResponse {
  submissionId: string;
  reference: string | null;
  submittedAt: string;
  confirmationMessage: string;
  redirectUrl: string | null;
  pdfUrl: string | null;
}

/** Exported so the portal entry point (src/portal/) shares one client rather than duplicating it. */
export const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    // The respondent session is an httpOnly cookie; without this the identity gate would verify
    // successfully and then every subsequent request would arrive anonymous.
    credentials: "same-origin",
    headers: init?.body ? { "content-type": "application/json" } : undefined,
  });

  const body: unknown = await response.json().catch(() => undefined);

  if (!response.ok) {
    const error = body as { error?: { code?: string; message?: string; details?: unknown } };
    throw Object.assign(new Error(error?.error?.message ?? "Something went wrong"), {
      code: error?.error?.code,
      details: error?.error?.details,
      status: response.status,
    });
  }

  return body as T;
};

export const fetchForm = (slug: string): Promise<PublicFormResponse> =>
  request<PublicFormResponse>(`/public/forms/${encodeURIComponent(slug)}`);

/** The staff-only draft preview — see PreviewFlow.tsx for why this is a separate fetch and a
 *  separate renderer entirely, not a flag on the normal applicant path. */
export const fetchFormPreview = (slug: string, token: string): Promise<PublicFormResponse> =>
  request<PublicFormResponse>(
    `/public/forms/${encodeURIComponent(slug)}/preview?token=${encodeURIComponent(token)}`,
  );

export const saveDraft = (
  slug: string,
  payload: { draftId?: string; data: Record<string, unknown>; currentPage: number },
): Promise<{ draftId: string; lastSavedAt: string; expiresAt: string }> =>
  request(`/public/forms/${encodeURIComponent(slug)}/draft`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const submitForm = (
  slug: string,
  payload: {
    draftId?: string;
    data: Record<string, unknown>;
    consents: { policyKey: string; policyVersion: string; accepted: boolean }[];
    turnstileToken?: string;
    elapsedSeconds: number;
    website?: string;
  },
): Promise<SubmitResponse> =>
  request(`/public/forms/${encodeURIComponent(slug)}/submit`, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const fetchStates = (): Promise<{ data: { code: string; name: string }[] }> =>
  request("/public/lookup/states");

export const fetchLgas = (state: string): Promise<{ data: { id: string; name: string }[] }> =>
  request(`/public/lookup/lgas?state=${encodeURIComponent(state)}`);

// ─── Respondent identity (Phase 6) ───────────────────────────────────────────

export interface PrefillResponse {
  values: Record<string, unknown>;
  provenance: Record<string, { cycle: string | null; updatedAt: string }>;
  existingSubmission: {
    id: string;
    reference: string | null;
    submittedAt: string;
    canEdit: boolean;
  } | null;
  respondent: { displayName: string | null; email: string };
}

export const requestOtp = (
  formSlug: string | undefined,
  email: string,
): Promise<{ message: string; expiresInMinutes: number; devOnlyCode?: string }> =>
  request("/public/otp/request", { method: "POST", body: JSON.stringify({ email, formSlug }) });

export const logout = (): Promise<Record<string, never>> =>
  request("/public/otp/logout", { method: "POST" });

export const verifyOtp = (
  email: string,
  code: string,
): Promise<{ respondent: { email: string; displayName: string | null } }> =>
  request("/public/otp/verify", { method: "POST", body: JSON.stringify({ email, code }) });

export const fetchPrefill = (slug: string): Promise<PrefillResponse> =>
  request(`/public/forms/${encodeURIComponent(slug)}/prefill`);

// ─── Changing an answer after submitting ─────────────────────────────────────

export interface MySubmission {
  id: string;
  reference: string | null;
  formTitle: string;
  formSlug: string;
  submittedAt: string | null;
  canEdit: boolean;
  /** Why not, when `canEdit` is false. An applicant is owed a reason, not a missing button. */
  reason: string | null;
}

export interface EditPayload {
  submission: {
    id: string;
    reference: string | null;
    submittedAt: string | null;
    cycle: string | null;
    canEdit: boolean;
    reason: string | null;
  };
  form: { id: string; slug: string; title: string };
  schema: {
    fields: PublicField[];
    sections: PublicSection[];
    logic: unknown[];
  };
  answers: Record<string, unknown>;
}

/** Whether the signed-in applicant has already applied to this form, and may still change it. */
export const fetchMySubmission = (slug: string): Promise<{ submission: MySubmission | null }> =>
  request(`/public/forms/${encodeURIComponent(slug)}/my-submission`);

export const fetchSubmissionForEdit = (submissionId: string): Promise<EditPayload> =>
  request(`/public/submissions/${encodeURIComponent(submissionId)}/edit`);

export const saveSubmissionEdit = (
  submissionId: string,
  data: Record<string, unknown>,
): Promise<{ id: string; reference: string | null; changedKeys: string[] }> =>
  request(`/public/submissions/${encodeURIComponent(submissionId)}`, {
    method: "PUT",
    body: JSON.stringify({ data }),
  });
